import React, {useRef, useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import styled from "styled-components";
import {WATERSPEED_PRIVACY_LINK, WATERSPEED_TERMS_LINK} from "constants/AppConstants.js";
import AuthService from "services/AuthService";
import {getEmailProviders} from "services/EmailProviderService";
import type {AuthProvider} from "services/EmailProviderService";
import {ws} from "styles/tokens";
import {
  CardSubtitle,
  CardTitle,
  CtaArrowIcon,
  FormInput,
  FormInputBox,
  FormLabel,
  GradientCtaButton,
  LinkButton,
  OnboardingCard,
  ONBOARDING_COLORS,
  SwitchRow
} from "./onboardingStyles";

const MIN_PASSWORD_LENGTH = 8;

const PROVIDER_LABELS: Partial<Record<AuthProvider, string>> = {
  google: "Google",
  apple: "Apple",
  facebook: "Facebook"
};

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  width: 100%;
  max-width: 370px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
`;

const HideButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: ${ws.fontBody};
  font-size: 12px;
  font-weight: 600;
  color: ${ONBOARDING_COLORS.subtitle};
  cursor: pointer;
`;

const AgreeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const CheckboxButton = styled.button<{checked: boolean}>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: 1.5px solid ${(props) => (props.checked ? ws.accent : ws.lineStrong)};
  border-radius: ${ws.radiusXs};
  background: ${(props) => (props.checked ? ws.accent : ws.card)};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ws.onAccent};
  font-size: 14px;
  line-height: 1;
`;

const AgreeText = styled.p`
  margin: 0;
  max-width: 300px;
  font-family: ${ws.fontBody};
  font-size: 12px;
  color: ${ws.muted};

  a {
    font-weight: 600;
    color: ${ONBOARDING_COLORS.accentBright};
    text-decoration: none;
  }
`;

const ErrorText = styled.span`
  font-family: ${ws.fontBody};
  font-size: 13px;
  color: ${ws.danger};
`;

const InfoText = styled.span`
  font-family: ${ws.fontBody};
  font-size: 13px;
  color: ${ws.muted};
`;

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

export type AccountMode = "signup" | "login";

interface Props {
  onSubmit: (fields: {name: string; email: string; password: string}) => Promise<void>;
  onLogin: (fields: {email: string; password: string}) => Promise<void>;
  initialMode?: AccountMode;
  isSubmitting: boolean;
}

function CreateAccountStep({onSubmit, onLogin, initialMode = "signup", isSubmitting}: Props) {
  const {t} = useTranslation();
  const [mode, setMode] = useState<AccountMode>(initialMode);
  const [resetSent, setResetSent] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [takenBy, setTakenBy] = useState<AuthProvider[] | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  // Address `takenBy` belongs to, so a changed one is never judged by a stale result.
  const checkedEmail = useRef<string | null>(null);

  const takenMessage = () => {
    const provider = takenBy?.[0];
    const label = provider ? PROVIDER_LABELS[provider] : undefined;
    return label ? t("onboarding.emailInUseWith", {provider: label}) : t("onboarding.emailInUse");
  };

  /** True when the address is free, or when we could not find out. */
  async function checkEmailAvailable(candidate: string): Promise<boolean> {
    const normalized = candidate.trim().toLowerCase();
    if (!normalized) {
      return true;
    }
    if (checkedEmail.current === normalized) {
      return !takenBy?.length;
    }
    setIsChecking(true);
    const providers = await getEmailProviders(normalized);
    setIsChecking(false);
    checkedEmail.current = normalized;
    setTakenBy(providers);
    // An email/password account can log in right here instead of hitting a dead end.
    if (providers?.includes("email")) {
      setMode("login");
    }
    // null means the lookup failed — let Firebase rule on it instead.
    return !providers?.length;
  }

  function switchMode(next: AccountMode) {
    setError(null);
    setResetSent(false);
    setMode(next);
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError(t("auth.emailRequired"));
      return;
    }
    setError(null);
    const sent = await AuthService.sendPasswordResetLinkAsync(email.trim());
    if (sent) {
      setResetSent(true);
    } else {
      setError(t("messages.error"));
    }
  }

  async function handleLoginSubmit() {
    if (!email.trim()) {
      setError(t("auth.emailRequired"));
      return;
    }
    if (!password) {
      setError(t("auth.passwordRequired"));
      return;
    }
    setError(null);
    await onLogin({email: email.trim(), password});
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    if (checkedEmail.current !== value.trim().toLowerCase()) {
      setTakenBy(null);
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (mode === "login") {
      await handleLoginSubmit();
      return;
    }
    if (!email.trim()) {
      setError(t("auth.emailRequired"));
      return;
    }
    if (!name.trim()) {
      setError(t("auth.nameRequired"));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t("onboarding.passwordTooShort"));
      return;
    }
    if (!agreed) {
      setError(t("onboarding.agreeRequired"));
      return;
    }
    // Covers someone who never left the field; usually answered by the blur check.
    if (!(await checkEmailAvailable(email))) {
      setError(null);
      return;
    }
    setError(null);
    await onSubmit({name: name.trim(), email: email.trim(), password});
  }

  return (
    <OnboardingCard as="form" gap="32px" onSubmit={handleSubmit}>
      <HeaderGroup>
        <CardTitle>{mode === "login" ? t("auth.signInTo") : t("onboarding.createAccountTitle")}</CardTitle>
        <CardSubtitle>{t("onboarding.saveTracks")}</CardSubtitle>
      </HeaderGroup>
      <FieldsStack>
        <InputGroup>
          <FormLabel htmlFor="onboarding-email">{t("auth.email")}</FormLabel>
          <FormInputBox>
            <FormInput
              id="onboarding-email"
              type="email"
              autoComplete="email"
              placeholder={t("onboarding.emailPlaceholder")}
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={(e) => checkEmailAvailable(e.target.value)}
            />
          </FormInputBox>
          {!!takenBy?.length &&
            (mode === "login" && takenBy.includes("email") ? (
              <InfoText>{t("onboarding.emailInUse")}</InfoText>
            ) : (
              <ErrorText>{takenMessage()}</ErrorText>
            ))}
        </InputGroup>
        {mode === "signup" && (
          <InputGroup>
            <FormLabel htmlFor="onboarding-name">{t("onboarding.fullName")}</FormLabel>
            <FormInputBox>
              <FormInput
                id="onboarding-name"
                type="text"
                autoComplete="name"
                placeholder={t("onboarding.fullNamePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormInputBox>
          </InputGroup>
        )}
        <InputGroup>
          <FormLabel htmlFor="onboarding-password">{t("auth.password")}</FormLabel>
          <FormInputBox>
            <FormInput
              id="onboarding-password"
              type={showPassword ? "text" : "password"}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              placeholder={mode === "login" ? undefined : t("onboarding.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <HideButton type="button" onClick={() => setShowPassword((prev) => !prev)}>
              {showPassword ? t("onboarding.hide") : t("onboarding.show")}
            </HideButton>
          </FormInputBox>
          {mode === "login" && (
            <ForgotRow>
              <LinkButton type="button" onClick={handleForgotPassword}>
                {t("auth.forgotPasswordQuestion")}
              </LinkButton>
            </ForgotRow>
          )}
        </InputGroup>
        {mode === "signup" && (
          <AgreeRow>
            <CheckboxButton
              type="button"
              checked={agreed}
              aria-checked={agreed}
              role="checkbox"
              onClick={() => setAgreed((prev) => !prev)}
            >
              {/* eslint-disable-next-line i18next/no-literal-string */}
              {agreed ? "✓" : ""}
            </CheckboxButton>
            <AgreeText>
              <Trans
                i18nKey="onboarding.agreeCheckbox"
                components={{
                  /* eslint-disable jsx-a11y/anchor-has-content -- Trans injects the anchor text */
                  privacy: <a href={WATERSPEED_PRIVACY_LINK} target="_blank" rel="noreferrer" />,
                  terms: <a href={WATERSPEED_TERMS_LINK} target="_blank" rel="noreferrer" />
                  /* eslint-enable jsx-a11y/anchor-has-content */
                }}
              />
            </AgreeText>
          </AgreeRow>
        )}
        {resetSent && <InfoText>{t("messages.resetPasswordSuccess")}</InfoText>}
        {error && <ErrorText>{error}</ErrorText>}
      </FieldsStack>
      <GradientCtaButton type="submit" disabled={isSubmitting || isChecking}>
        {mode === "login" ? t("onboarding.logIn") : t("onboarding.register")}
        <CtaArrowIcon src="/assets/images/onboarding/arrow-cta.svg" alt="" />
      </GradientCtaButton>
      {mode === "signup" ? (
        <SwitchRow>
          {t("onboarding.alreadyHaveAccount")}{" "}
          <LinkButton type="button" onClick={() => switchMode("login")}>
            {t("onboarding.logIn")}
          </LinkButton>
        </SwitchRow>
      ) : (
        <SwitchRow>
          {t("onboarding.newHere")}{" "}
          <LinkButton type="button" onClick={() => switchMode("signup")}>
            {t("onboarding.createAccountTitle")}
          </LinkButton>
        </SwitchRow>
      )}
    </OnboardingCard>
  );
}

export default CreateAccountStep;
